import {cpServiceGet} from 'api';
import {camelizeKeys} from 'utils/format-strings';
import {sendSentryException} from 'utils/error-handling';
import {
    RawComparativeSnapshotDataResponse,
    Microclimates,
} from 'api/rates/comparative-snapshot/comparative-snapshot.types';

const transformLabel = (label: string): string => {
    return [...label]
        .map(
            (char) =>
                char.toLowerCase() === 'h'
                    ? char // Keep 'h' as is
                    : char.toUpperCase() // Make other characters uppercase
        )
        .join('');
};

/**
 * Formats the response from the comparative snapshot API.
 */
const formatResponse = (
    response: RawComparativeSnapshotDataResponse
): Microclimates => {
    const formattedResponse: Microclimates = camelizeKeys(
        response.microclimates
    );

    // Iterate over each radius in the `Microclimates` object
    Object.keys(formattedResponse).forEach((radiusKey) => {
        const segments = formattedResponse[radiusKey as keyof Microclimates]; // Get the array of MicroclimateSegments

        if (segments) {
            // Transform the `label` inside `rateSegment` for each segment
            formattedResponse[radiusKey as keyof Microclimates] = segments.map(
                (segment) => ({
                    ...segment,
                    rateSegment: {
                        ...segment.rateSegment,
                        label: transformLabel(segment.rateSegment.label), // Apply transformation
                    },
                })
            );
        }
    });

    return formattedResponse;
};

/**
 * Gets the comparative snapshot for a facility
 */
const getComparativeSnapshot = async (
    facilityId: number
): Promise<Microclimates> => {
    try {
        const response = await cpServiceGet<RawComparativeSnapshotDataResponse>(
            `facilities/${facilityId}/competitive-snapshot`
        );

        const formattedResponse = formatResponse(response.data.data);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);
        throw error;
    }
};

export default getComparativeSnapshot;
